import { solveFixedEndpointsTspWithCost } from './fixedEndpointsTsp';

const MAX_DIRECTIONS_WAYPOINTS = 23; // Google Directions allows 25 total; reserve headroom

function latLng(point) {
  return { lat: point.lat, lng: point.lng };
}

function getDirectionsService() {
  if (!window.google?.maps?.DirectionsService) return null;
  return new window.google.maps.DirectionsService();
}

function getDistanceMatrixService() {
  if (!window.google?.maps?.DistanceMatrixService) return null;
  return new window.google.maps.DistanceMatrixService();
}

/** Format seconds as e.g. "2h 15m" or "42m". */
export function formatDriveDuration(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  if (s < 60) return `${s}s`;
  const hours = Math.floor(s / 3600);
  const minutes = Math.round((s % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function parseDirectionsRoute(route, waypoints) {
  const order = route.waypoint_order ?? waypoints.map((_, i) => i);
  const orderedStops = order.map((idx) => waypoints[idx]);
  const path = route.overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
  const totalSeconds = route.legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);
  const totalMeters = route.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
  return {
    orderedStops,
    path,
    totalSeconds,
    totalMiles: totalMeters / 1609.344,
  };
}

function requestDirections({ origin, destination, waypoints, optimizeWaypoints }) {
  return new Promise((resolve, reject) => {
    const service = getDirectionsService();
    if (!service) {
      reject(new Error('Google Directions is not available.'));
      return;
    }
    service.route(
      {
        origin: latLng(origin),
        destination: latLng(destination),
        waypoints: waypoints.map((wp) => ({ location: latLng(wp), stopover: true })),
        optimizeWaypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK' && result?.routes?.[0]) {
          resolve(result.routes[0]);
        } else {
          reject(new Error(`Directions request failed (${status}).`));
        }
      }
    );
  });
}

/** Fetch driving-time matrix (seconds) for an ordered list of points. */
function fetchDurationMatrix(points) {
  return new Promise((resolve, reject) => {
    const service = getDistanceMatrixService();
    if (!service) {
      reject(new Error('Google Distance Matrix is not available.'));
      return;
    }
    service.getDistanceMatrix(
      {
        origins: points.map(latLng),
        destinations: points.map(latLng),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (status !== 'OK' || !response?.rows?.length) {
          reject(new Error(`Distance Matrix request failed (${status}).`));
          return;
        }
        const n = points.length;
        const matrix = Array.from({ length: n }, () => Array(n).fill(Infinity));
        response.rows.forEach((row, i) => {
          row.elements.forEach((el, j) => {
            if (el.status === 'OK' && el.duration?.value != null) {
              matrix[i][j] = el.duration.value;
            }
          });
          matrix[i][i] = 0;
        });
        resolve(matrix);
      }
    );
  });
}

/**
 * Optimize stop order for minimum total driving time (fixed start & end), then fetch road geometry.
 * @param {{ lat: number, lng: number }} start
 * @param {{ lat: number, lng: number }} end
 * @param {Array<{ id: string, lat: number, lng: number, label?: string }>} waypoints
 */
export async function optimizeDrivingRouteByTime(start, end, waypoints) {
  if (!waypoints.length) {
    const route = await requestDirections({
      origin: start,
      destination: end,
      waypoints: [],
      optimizeWaypoints: false,
    });
    const parsed = parseDirectionsRoute(route, []);
    return { ...parsed, orderedStops: [] };
  }

  if (waypoints.length <= MAX_DIRECTIONS_WAYPOINTS) {
    const route = await requestDirections({
      origin: start,
      destination: end,
      waypoints,
      optimizeWaypoints: true,
    });
    return parseDirectionsRoute(route, waypoints);
  }

  // Many stops: build a driving-time matrix, run TSP on durations, then request directions in that order.
  const points = [start, ...waypoints, end];
  const matrix = await fetchDurationMatrix(points);
  const orderIndices = solveFixedEndpointsTspWithCost(
    0,
    points.length - 1,
    waypoints.map((_, i) => i + 1),
    matrix
  );
  const orderedStops = orderIndices.map((idx) => waypoints[idx - 1]);
  const route = await requestDirections({
    origin: start,
    destination: end,
    waypoints: orderedStops,
    optimizeWaypoints: false,
  });
  return parseDirectionsRoute(route, orderedStops);
}
